import { useState, useEffect } from 'react';
import { pressReleaseService } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';

const PressReleaseForm = ({ release, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: [''],
    pdfUrl: '',
    webUrl: '',
    publishDate: '',
    author: 'Green TERRE Foundation',
    tags: '',
    isPublished: true
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (release) {
      setFormData({
        title: release.title || '',
        summary: release.summary || '',
        content: release.content?.length > 0 ? release.content : [''],
        pdfUrl: release.pdfUrl || '',
        webUrl: release.webUrl || '',
        publishDate: release.publishDate 
          ? new Date(release.publishDate).toISOString().split('T')[0] 
          : '',
        author: release.author || 'Green TERRE Foundation',
        tags: release.tags?.join(', ') || '',
        isPublished: release.isPublished !== false
      });
    }
  }, [release]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (index, value) => {
    const newContent = [...formData.content];
    newContent[index] = value;
    setFormData(prev => ({ ...prev, content: newContent }));
  };

  const addContentParagraph = () => {
    setFormData(prev => ({ ...prev, content: [...prev.content, ''] }));
  };

  const removeContentParagraph = (index) => {
    if (formData.content.length > 1) {
      const newContent = formData.content.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, content: newContent }));
    }
  };

  const handleThumbnailChange = (e) => {
    setThumbnail(e.target.files[0]);
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setRemovePdf(false);
    }
  };

  const handleRemovePdf = () => {
    setPdfFile(null);
    setRemovePdf(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('summary', formData.summary);
      formDataToSend.append('pdfUrl', formData.pdfUrl);
      formDataToSend.append('webUrl', formData.webUrl);
      formDataToSend.append('publishDate', formData.publishDate);
      formDataToSend.append('author', formData.author);
      formDataToSend.append('isPublished', formData.isPublished);
      
      // Handle tags
      if (formData.tags) {
        formDataToSend.append('tags', JSON.stringify(
          formData.tags.split(',').map(t => t.trim()).filter(Boolean)
        ));
      }
      
      // Handle content paragraphs
      formData.content.forEach(para => {
        if (para.trim()) {
          formDataToSend.append('content', para);
        }
      });

      // Handle thumbnail
      if (thumbnail) {
        formDataToSend.append('thumbnail', thumbnail);
      }

      // Handle PDF file
      if (pdfFile) {
        formDataToSend.append('pdfFile', pdfFile);
      }

      // Handle PDF removal
      if (removePdf) {
        formDataToSend.append('removePdf', 'true');
      }

      if (release) {
        await pressReleaseService.updatePressRelease(release._id, formDataToSend);
      } else {
        await pressReleaseService.createPressRelease(formDataToSend);
      }

      onSave();
    } catch (err) {
      console.error('Error saving press release:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save press release');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title *
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
          placeholder="Press release title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Summary *
        </label>
        <textarea
          name="summary"
          value={formData.summary}
          onChange={handleChange}
          required
          rows={3}
          maxLength={500}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
          placeholder="Brief summary (max 500 characters)"
        />
        <p className="text-xs text-gray-500 mt-1">
          {formData.summary.length}/500 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Content Paragraphs
        </label>
        {formData.content.map((para, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <textarea
              value={para}
              onChange={(e) => handleContentChange(index, e.target.value)}
              rows={3}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
              placeholder={`Paragraph ${index + 1}`}
            />
            {formData.content.length > 1 && (
              <button
                type="button"
                onClick={() => removeContentParagraph(index)}
                className="px-2 py-1 text-red-600 hover:text-red-800"
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addContentParagraph}
          className="text-sm text-[#166534] hover:underline"
        >
          + Add paragraph
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Thumbnail Image
        </label>
        {release?.thumbnail && (
          <div className="mb-2">
            <img
              src={getImageSrc(release.thumbnail)}
              alt="Current thumbnail"
              className="h-20 object-cover rounded"
            />
            <p className="text-xs text-gray-500">Current image</p>
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleThumbnailChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            PDF URL (external link)
          </label>
          <input
            type="url"
            name="pdfUrl"
            value={formData.pdfUrl}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
            placeholder="https://example.com/document.pdf"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Web URL
          </label>
          <input
            type="url"
            name="webUrl"
            value={formData.webUrl}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
            placeholder="https://example.com/release"
          />
        </div>
      </div>

      {/* PDF File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          PDF Attachment (upload file)
        </label>
        {release?.pdfFile && !removePdf && (
          <div className="mb-2 flex items-center gap-2">
            <a 
              href={release.pdfFile.startsWith('/') ? `${process.env.REACT_APP_API_BASE_URL || ''}${release.pdfFile}` : release.pdfFile}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#166534] hover:underline text-sm"
            >
              📄 Current PDF attached
            </a>
            <button
              type="button"
              onClick={handleRemovePdf}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Remove
            </button>
          </div>
        )}
        <input
          type="file"
          accept="application/pdf"
          onChange={handlePdfChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
        {pdfFile && (
          <p className="mt-1 text-sm text-gray-500">
            New PDF selected: {pdfFile.name}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Publish Date *
          </label>
          <input
            type="date"
            name="publishDate"
            value={formData.publishDate}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Author
          </label>
          <input
            type="text"
            name="author"
            value={formData.author}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
            placeholder="Author name"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tags (comma-separated)
        </label>
        <input
          type="text"
          name="tags"
          value={formData.tags}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent"
          placeholder="climate, sustainability, campus"
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          name="isPublished"
          id="isPublished"
          checked={formData.isPublished}
          onChange={handleChange}
          className="h-4 w-4 text-[#166534] focus:ring-[#166534] border-gray-300 rounded"
        />
        <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-700">
          Publish immediately
        </label>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 bg-[#166534] text-white py-2 px-4 rounded-lg hover:bg-[#0e3d1f] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : (release ? 'Update' : 'Create')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default PressReleaseForm;
